import PillButton from "@/app/shared/PillButton";
import {
  EmailLoginViewModel,
  EmailLoginViewModelSchema,
} from "@/auth/models/EmailLoginViewModel";
import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@mui/material/Box";
import { FormProvider, useForm } from "react-hook-form";
import ControlledTextField from "../sell/ad/form/ControlledTextField";
import { useRef, useState } from "react";
import { Alert, styled, Typography } from "@mui/material";
import { FindUser } from "@/auth/actions/FindUser";
import { FindUserResponse } from "@/auth/models/FindUserViewModel";
import EmailLoginForm from "./EmailLoginForm";
import PasswordLoginForm from "./PasswordLoginForm";

const TypographyLink = styled(Typography)({
  cursor: "pointer",
  textDecoration: "underline",
  color: "black",
});

export default function EmailLookupForm({
  onBack,
  onLookup,
}: {
  onBack: () => void;
  onLookup: () => void;
}) {
  const [response, setResponse] = useState<FindUserResponse | null>(null);
  const [isForceEmailLogin, setIsForceEmailLogin] = useState(false);
  const methods = useForm<EmailLoginViewModel>({
    resolver: zodResolver(EmailLoginViewModelSchema),
    defaultValues: {
      emailOrUsername: "",
    },
  });
  const emailOrUsername = methods.getValues("emailOrUsername");
  const formRef = useRef<HTMLFormElement>(null);
  const isEmailLogin =
    response &&
    response.canLoginWithoutPassword &&
    (isForceEmailLogin || !response?.canLoginWithPassword);
  const canLogin =
    response &&
    (response.canLoginWithPassword || response.canLoginWithoutPassword);
  const cannotLogin = response && !canLogin;
  return (
    <FormProvider {...methods}>
      {canLogin && (
        <>
          <TypographyLink
            onClick={() => {
              setResponse(null);
              setIsForceEmailLogin(false);
              onBack();
            }}
            sx={{
              alignSelf: "flex-start",
            }}
          >
            &lt; Back
          </TypographyLink>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {isEmailLogin ? "Login without a password" : "Login with password"}
          </Typography>
        </>
      )}
      <Box
        component={canLogin ? "div" : "form"}
        sx={{
          padding: 4,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "100%",
          backgroundColor: "white",
          alignItems: "center",
          borderRadius: 5,
        }}
        ref={formRef}
        onSubmit={methods.handleSubmit(async (data) => {
          const response = await FindUser({
            emailOrUsername: data.emailOrUsername,
          });
          setResponse(response);
          const canLogin =
            response &&
            (response.canLoginWithPassword || response.canLoginWithoutPassword);
          if (canLogin) {
            onLookup();
          }
        })}
      >
        {canLogin ? (
          <>
            {isEmailLogin ? (
              <EmailLoginForm emailOrUsername={emailOrUsername} />
            ) : (
              <PasswordLoginForm
                defaultEmailOrUsername={emailOrUsername}
                onForgotClicked={(emailOrUsername) => {
                  methods.setValue("emailOrUsername", emailOrUsername);
                  setIsForceEmailLogin(true);
                }}
              />
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              alignItems: "center",
            }}
          >
            <ControlledTextField
              name="emailOrUsername"
              label="Email or Username"
              type="text"
              fullWidth
            />
            <PillButton
              primary
              type="submit"
              loading={methods.formState.isSubmitting}
              disabled={methods.formState.isSubmitting}
            >
              Next
            </PillButton>
            {cannotLogin && (
              <Alert severity="error">
                No account found. Please check your email/username or sign up
                for a new account.
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </FormProvider>
  );
}
